import { useEffect, useState } from 'react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useCopilotLayout } from '@/shared/store/copilotLayoutStore';
import { ASSISTANT_NAME } from '@/shared/branding';

type Theme = {
  key: string; label: string; primary: string; primaryDark: string; primaryLight: string; strong: string;
  // The theme tints the system/page background; cards stay white and the nav
  // bar stays near-white so the white menu buttons read cleanly.
  canvas: string; canvasSoft: string; navBg: string;
};

// Full app themes — one disciplined accent each. The accent drives buttons,
// links, document numbers, focus rings, checkmarks and checkboxes; the canvas
// trio tints the system background.
const THEMES: Theme[] = [
  { key: 'teal',       label: 'Teal',       primary: '#1f8ba3', primaryDark: '#12768d', primaryLight: '#dcf0f5', strong: '#0c5e72', canvas: '#e9f2f4', canvasSoft: '#d8e8ea', navBg: '#ffffff' },
  { key: 'indigo',     label: 'Indigo',     primary: '#6d61d6', primaryDark: '#574bc4', primaryLight: '#eae8fc', strong: '#4738a8', canvas: '#edecf8', canvasSoft: '#e0ddf3', navBg: '#ffffff' },
  { key: 'nexus-blue', label: 'NextGen Blue', primary: '#4a97ff', primaryDark: '#2a7cf0', primaryLight: '#e6f1ff', strong: '#1a5fc0', canvas: '#e9f2fd', canvasSoft: '#dcebfb', navBg: '#ffffff' },
  { key: 'tan',        label: 'Tan',        primary: '#c99e5b', primaryDark: '#b08440', primaryLight: '#f7f0df', strong: '#8a6630', canvas: '#f5efe3', canvasSoft: '#ede4d1', navBg: '#ffffff' },
  { key: 'emerald',    label: 'Emerald',    primary: '#2bb588', primaryDark: '#179a70', primaryLight: '#dbf4ea', strong: '#0d7250', canvas: '#e8f4ed', canvasSoft: '#d7ebe1', navBg: '#ffffff' },
  { key: 'slate',      label: 'Slate',      primary: '#64748b', primaryDark: '#4f5d72', primaryLight: '#e8ecf2', strong: '#3a4659', canvas: '#eaeef2', canvasSoft: '#dde3ea', navBg: '#ffffff' },
  { key: 'midnight',   label: 'Midnight',   primary: '#3f6bce', primaryDark: '#2d54ad', primaryLight: '#e3ebfb', strong: '#21408a', canvas: '#e4ebf8', canvasSoft: '#d9e3f3', navBg: '#ffffff' },
  { key: 'plum',       label: 'Plum',       primary: '#b85585', primaryDark: '#9c416d', primaryLight: '#f6e4ed', strong: '#762f51', canvas: '#f0e4ea', canvasSoft: '#e8d9e2', navBg: '#ffffff' },
];

// Map retired accent presets onto the closest full theme so existing users
// don't lose their selection when this list shrank to three themes.
const LEGACY_THEME: Record<string, string> = {
  'mrpcomp-blue': 'nexus-blue', ocean: 'nexus-blue', violet: 'indigo',
  forest: 'teal', sunset: 'teal', crimson: 'teal', graphite: 'teal',
};
const normalizeTheme = (k: string) =>
  (k === 'custom' || THEMES.some((t) => t.key === k) ? k : (LEGACY_THEME[k] || 'teal'));

// ---- Custom theme: derive the full token set from ONE accent color. ----
function hexToHsl(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return [192, 88, 28];
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, Math.round(l * 100)];
  const d = max - min;
  const sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0));
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [Math.round(h * 60), Math.round(sat * 100), Math.round(l * 100)];
}
const hsl = (h: number, s: number, l: number) => `hsl(${h}, ${Math.max(0, Math.min(100, s))}%, ${Math.max(0, Math.min(100, l))}%)`;
function customThemeFrom(color: string): Theme {
  const [h, sat, l] = hexToHsl(color);
  return {
    key: 'custom', label: 'Custom',
    primary: color,
    primaryDark: hsl(h, sat, Math.max(l - 9, 8)),
    primaryLight: hsl(h, Math.min(sat, 55), 93),
    strong: hsl(h, Math.min(sat + 5, 100), Math.max(l - 18, 12)),
    canvas: hsl(h, 32, 94),
    canvasSoft: hsl(h, 34, 89),
    navBg: '#ffffff',
  };
}

const FONT_SIZES = [
  { key: 'S', value: 13, label: 'S' },
  { key: 'M', value: 14, label: 'M' },
  { key: 'L', value: 15, label: 'L' },
  { key: 'XL', value: 16, label: 'XL' },
];

const STORAGE_KEY = 'mrpcomp:appearance';

type Prefs = { theme: string; fontSize: number; customColor?: string; font?: string };

// Selectable UI typefaces (all self-hosted; Rubik doubles as the non-latin
// fallback for every choice).
export const FONT_OPTIONS: Array<{ key: string; label: string; stack: string; note: string }> = [
  // First entry = the default for users without a saved font preference.
  { key: 'system', label: 'System (sharp)', stack: "system-ui, -apple-system, 'Segoe UI', Roboto, 'Rubik', sans-serif", note: 'Native OS font — crispest rendering' },
  { key: 'dm-sans', label: 'DM Sans', stack: "'DM Sans', 'Rubik', system-ui, -apple-system, sans-serif", note: 'Geometric, friendly' },
  { key: 'inter', label: 'Inter', stack: "'Inter', 'Rubik', system-ui, -apple-system, sans-serif", note: 'Neutral, engineered for screens' },
  { key: 'rubik', label: 'Rubik', stack: "'Rubik', system-ui, -apple-system, sans-serif", note: 'Rounded, multilingual' },
  { key: 'jakarta', label: 'Plus Jakarta Sans', stack: "'Plus Jakarta Sans', 'Rubik', system-ui, -apple-system, sans-serif", note: 'Modern, slightly wide' },
];

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) { const p = JSON.parse(raw); return { theme: normalizeTheme(p.theme), fontSize: p.fontSize ?? 14, customColor: p.customColor, font: p.font }; }
  } catch (_) { /* ignore */ }
  return { theme: 'teal', fontSize: 14 };
}

export function applyAppearance(prefs: Prefs) {
  const theme = normalizeTheme(prefs.theme) === 'custom' && prefs.customColor
    ? customThemeFrom(prefs.customColor)
    : THEMES.find((t) => t.key === normalizeTheme(prefs.theme)) || THEMES[0];
  const root = document.documentElement;
  root.style.setProperty('--primary', theme.primary);
  root.style.setProperty('--primary-dark', theme.primaryDark);
  root.style.setProperty('--primary-light', theme.primaryLight);
  root.style.setProperty('--accent-strong', theme.strong);
  // System background carries the theme tint (menu buttons stay white).
  root.style.setProperty('--canvas', theme.canvas);
  root.style.setProperty('--canvas-soft', theme.canvasSoft);
  root.style.setProperty('--nav-bg', theme.navBg);
  // Text size: the ERP's text is rem (the Tailwind type tokens and, after
  // Wave 4's codemod, every text size), so the <html> font size scales it.
  // 14 (M) is the default: no override, the browser's own root size stays.
  // Control heights are px (h-[26px]) and keep their size. This replaces the
  // old CSS zoom on <body>, which scaled icons, paddings and controls too.
  root.style.fontSize = prefs.fontSize === 14 ? '' : `${+((prefs.fontSize / 14) * 100).toFixed(4)}%`;
  document.body.style.removeProperty('zoom');
  // UI typeface: swap the one token every element inherits from.
  const font = FONT_OPTIONS.find((f) => f.key === prefs.font) || FONT_OPTIONS[0];
  root.style.setProperty('--font-main', font.stack);
}

export function initAppearance() {
  applyAppearance(loadPrefs());
}

type PageProps = { embedded?: boolean };

const COPILOT_LAYOUTS = [
  { key: 'floating' as const, label: 'Floating bubble', desc: 'A button bottom-right that opens a slide-over chat (⌘K).' },
  { key: 'docked' as const, label: 'Docked panel', desc: 'A panel always shown on the right; the page makes room for it.' },
];

export function AppearancePage({ embedded = false }: PageProps = {}) {
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
  const copilotLayout = useCopilotLayout((s) => s.layout);
  const setCopilotLayout = useCopilotLayout((s) => s.setLayout);

  // Auto-apply AND auto-persist on every change. The earlier flow gated
  // localStorage behind a Save button — it confused users who saw the
  // theme change live but lost their pick on reload because they hadn't
  // realised they needed to click Save.
  // Persistence now goes through savePrefs(), which writes to BOTH the
  // localStorage cache (for instant cold-boot apply) AND the server
  // (so the choice follows the user across browsers). The 200 ms debounce
  // collapses rapid theme/font clicks into one PUT.
  useEffect(() => {
    applyAppearance(prefs);
    const t = setTimeout(() => { void savePrefs(prefs); }, 200);
    return () => clearTimeout(t);
  }, [prefs]);

  function reset() {
    setPrefs({ theme: 'teal', fontSize: 14 });
  }
  const activeTheme = normalizeTheme(prefs.theme);

  return (
    <div>
      {!embedded && <PageHeader title="Appearance" description="Personalize your theme and type scale." />}

      <div className={embedded ? 'space-y-1' : 'grid lg:grid-cols-[1fr_360px] gap-1'}>
        <div className="space-y-5">
          <Card title="Theme">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {THEMES.map((t) => {
                const active = activeTheme === t.key;
                return (
                  <button key={t.key}
                    onClick={() => setPrefs({ ...prefs, theme: t.key })}
                    className={`p-2 rounded-lg border text-left transition ${
                      active ? 'border-primary ring-2 ring-primary/15 bg-primary-light/40' : 'border-border hover:border-border-cool'
                    }`}>
                    <div className="flex items-center gap-2.5">
                      <span className="w-6 h-6 rounded-full shrink-0" style={{ background: t.primary, boxShadow: `0 0 0 3px ${t.primaryLight}` }} />
                      <div className="min-w-0">
                        <div className="text-[0.8125rem] font-semibold text-primary-text truncate flex items-center gap-1.5">
                          {t.label}{active && <span className="text-[0.625rem] font-semibold text-primary">●</span>}
                        </div>
                        <div className="text-meta text-muted font-numeric">{t.primary}</div>
                      </div>
                    </div>
                    {/* live mini-preview of the theme accent */}
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <span className="inline-flex items-center h-5 px-2 rounded-full text-[0.625rem] font-semibold text-white" style={{ background: t.primary }}>Button</span>
                      <span className="text-[0.6875rem] font-medium" style={{ color: t.primary }}>Link</span>
                      <span className="inline-grid place-items-center w-4 h-4 rounded-[5px] text-white text-[0.5625rem]" style={{ background: t.primary }}>✓</span>
                      <span className="h-4 px-1.5 rounded text-[0.5625rem] font-semibold inline-flex items-center" style={{ background: t.primaryLight, color: t.strong }}>Tab</span>
                    </div>
                  </button>
                );
              })}
              {/* Custom accent: one color drives the whole derived token set. */}
              {(() => {
                const active = activeTheme === 'custom';
                const color = prefs.customColor || '#086f87';
                const t = customThemeFrom(color);
                return (
                  <button key="custom"
                    onClick={() => setPrefs({ ...prefs, theme: 'custom', customColor: color })}
                    className={`p-2 rounded-lg border text-left transition ${
                      active ? 'border-primary ring-2 ring-primary/15 bg-primary-light/40' : 'border-border hover:border-border-cool'
                    }`}>
                    <div className="flex items-center gap-2.5">
                      <label className="w-9 h-9 rounded-full shrink-0 cursor-pointer overflow-hidden relative"
                        style={{ background: color, boxShadow: `0 0 0 3px ${t.primaryLight}` }}
                        onClick={(e) => e.stopPropagation()}>
                        <input type="color" value={color}
                          onChange={(e) => setPrefs({ ...prefs, theme: 'custom', customColor: e.target.value })}
                          className="absolute inset-0 opacity-0 cursor-pointer" title="Pick a custom accent color" />
                      </label>
                      <div className="min-w-0">
                        <div className="text-[0.8125rem] font-semibold text-primary-text truncate flex items-center gap-1.5">
                          Custom{active && <span className="text-[0.625rem] font-semibold text-primary">●</span>}
                        </div>
                        <div className="text-meta text-muted font-numeric">{color} — click the swatch</div>
                      </div>
                    </div>
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <span className="inline-flex items-center h-5 px-2 rounded-full text-[0.625rem] font-semibold text-white" style={{ background: t.primary }}>Button</span>
                      <span className="text-[0.6875rem] font-medium" style={{ color: t.primary }}>Link</span>
                      <span className="inline-grid place-items-center w-4 h-4 rounded-[5px] text-white text-[0.5625rem]" style={{ background: t.primary }}>✓</span>
                      <span className="h-4 px-1.5 rounded text-[0.5625rem] font-semibold inline-flex items-center" style={{ background: t.primaryLight, color: t.strong }}>Tab</span>
                    </div>
                  </button>
                );
              })()}
            </div>
          </Card>

          <Card title="Font">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              {FONT_OPTIONS.map((f) => {
                const active = (prefs.font || 'dm-sans') === f.key;
                return (
                  <button key={f.key} onClick={() => setPrefs({ ...prefs, font: f.key })}
                    className={`p-2 rounded-lg border text-left transition ${
                      active ? 'border-primary bg-primary-light/50' : 'border-border hover:border-border-cool'
                    }`}
                    style={{ fontFamily: f.stack }}>
                    <div className="text-[0.9375rem] font-semibold text-primary-text flex items-center gap-1.5">
                      {f.label}{active && <span className="text-[0.625rem] font-semibold text-primary">●</span>}
                    </div>
                    <div className="text-[0.75rem] text-secondary mt-0.5">{f.note}</div>
                    <div className="text-[0.8125rem] text-primary-text mt-2">Invoice INV-26-000123 · $12,480.00</div>
                    <div className="text-meta text-muted">The quick brown fox jumps over 0123456789</div>
                  </button>
                );
              })}
            </div>
          </Card>

          <Card title="Font size">
            <div className="flex gap-2 flex-wrap">
              {FONT_SIZES.map((f) => {
                const active = prefs.fontSize === f.value;
                return (
                  <button key={f.key}
                    onClick={() => setPrefs({ ...prefs, fontSize: f.value })}
                    className={`px-5 py-2 rounded-xl border font-semibold transition ${
                      active ? 'bg-primary text-white border-primary' : 'bg-surface text-primary-text border-border hover:border-border-cool'
                    }`}
                    style={{ fontSize: f.value }}>
                    {f.label} <span className="ml-1 text-[0.6875rem] opacity-70 font-normal">{f.value}px</span>
                  </button>
                );
              })}
            </div>
          </Card>

          <Card title={`${ASSISTANT_NAME} panel`}>
            <div className="grid grid-cols-2 gap-2">
              {COPILOT_LAYOUTS.map((c) => {
                const active = copilotLayout === c.key;
                return (
                  <button key={c.key} onClick={() => setCopilotLayout(c.key)}
                    className={`p-2 rounded-lg border text-left transition ${
                      active ? 'border-primary bg-primary-light/50' : 'border-border hover:border-border-cool'
                    }`}>
                    <div className="text-[0.8125rem] font-semibold text-primary-text">{c.label}</div>
                    <div className="text-meta text-secondary mt-0.5">{c.desc}</div>
                  </button>
                );
              })}
            </div>
          </Card>
        </div>

        <Card title="Preview">
          <div className="space-y-3 text-[0.8125rem]">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-3 h-8 rounded-full bg-primary text-white font-semibold">Primary button</span>
              <span className="inline-flex items-center px-3 h-8 rounded-full bg-slate-100 text-primary-text border border-slate-300 font-semibold">Active tab</span>
            </div>
            <div className="rounded-xl border border-border p-3 bg-surface">
              <div className="text-[0.6875rem] uppercase tracking-wide text-muted font-semibold mb-1">Order #MO-00142</div>
              <div className="text-primary-text font-semibold">A00004 · Wooden table</div>
              <div className="text-secondary text-[0.75rem]">100 pcs planned · scheduled 2026-04-22 → 2026-04-24</div>
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={reset}>Reset to default</Button>
            </div>
            <div className="text-meta text-muted pt-2">
              Changes apply and save instantly. Preferences sync to your account, so they follow you to other browsers and devices.
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

// Persist preferences to the user's account AND to the localStorage
// cache. Called from the auto-save effect above (debounced) and any
// future programmatic mutators. localStorage stays the source of truth
// for the cold-boot apply path so we never flash unstyled content; the
// server copy is what makes the choice follow the user across devices.
async function savePrefs(p: Prefs) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(p)); } catch { /* quota / private mode */ }
  try {
    await fetch('/api/v1/user-preferences/appearance', {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(p),
    });
  } catch { /* offline / network — localStorage still has it */ }
}

// Fetch the current user's appearance prefs from the server and apply
// them. Called from useAuth after the /auth/me bootstrap resolves —
// overrides whatever initAppearance() applied from localStorage so a
// different browser picks up the user's last-known choice within one
// round-trip.
export async function syncAppearanceFromServer() {
  try {
    const res = await fetch('/api/v1/user-preferences/appearance', {
      credentials: 'same-origin',
    });
    if (!res.ok) return;
    const body = await res.json().catch(() => null) as { data: { value: Prefs } | null } | null;
    const raw = body?.data?.value;
    if (!raw) return;
    const value = { ...raw, fontSize: raw.fontSize ?? 14 };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch { /* */ }
    applyAppearance(value);
  } catch { /* offline — keep whatever initAppearance applied */ }
}
